import { useMenuImport } from "@/hooks/importers";

const DOORDASH_URL_PATTERN = /doordash\.com/i;
const UBER_EATS_URL_PATTERN = /ubereats\.com/i;

const validateDoorDashUrl = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return { valid: false, error: "validation.urlRequired" };
  if (!DOORDASH_URL_PATTERN.test(trimmed))
    return { valid: false, error: "validation.invalidUrl" };
  return { valid: true };
};

const validateUberEatsUrl = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return { valid: false, error: "validation.urlRequired" };
  if (!UBER_EATS_URL_PATTERN.test(trimmed))
    return { valid: false, error: "validation.invalidUrlUbereats" };
  return { valid: true };
};

const validateDoorDashForm = (form) => validateDoorDashUrl(form.url);

const validateUberEatsForm = (form) => validateUberEatsUrl(form.url);

/** OrderingPlus business API URL: path must include /business/{segment}. */
const validateOrderingBusinessUrl = (rawUrl) => {
  const trimmed = (rawUrl || "").trim();
  if (!trimmed) return { valid: false, error: "validation.urlRequired" };
  try {
    const u = new URL(trimmed);
    const marker = "/business/";
    const idx = u.pathname.indexOf(marker);
    if (idx === -1)
      return { valid: false, error: "validation.orderingInvalidUrl" };
    const after = u.pathname.slice(idx + marker.length).replace(/\/$/, "");
    const segment = after.split("/").filter(Boolean)[0];
    if (!segment)
      return { valid: false, error: "validation.orderingInvalidUrl" };
    return { valid: true };
  } catch {
    return { valid: false, error: "validation.orderingInvalidUrl" };
  }
};

const validateOrderingForm = (form) => {
  const url = (form.url || "").trim();
  const projectId = (form.orderingPlusProjectId || "").trim();
  const segment = (form.orderingBusinessSegment || "").trim();

  if (url) return validateOrderingBusinessUrl(url);
  if (projectId && segment) return { valid: true };
  if (projectId || segment)
    return { valid: false, error: "validation.orderingIncompleteStructured" };
  return { valid: false, error: "validation.orderingSourceRequired" };
};

const GLORIAFOOD_ALLOWED_HOSTS = new Set([
  "www.onlineorderprologin.com",
  "onlineorderprologin.com",
]);
const GLORIAFOOD_SHORT_URL_PATH_PREFIX = "/api/fb/";

const GLORIAFOOD_MENU_QUERY_KEYS = ["company_uid", "restaurant_uid"];

const hasGloriaFoodMenuQueryIds = (searchParams) =>
  GLORIAFOOD_MENU_QUERY_KEYS.every((key) => {
    const values = searchParams.getAll(key).filter(Boolean);
    return values.length === 1 && values[0].trim().length > 0;
  });

const validateGloriaFoodUrl = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed)
    return { valid: false, error: "validation.gloriafoodUrlRequired" };

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:") {
      return { valid: false, error: "validation.invalidUrlGloriafood" };
    }

    const host = parsed.hostname.toLowerCase();
    if (!GLORIAFOOD_ALLOWED_HOSTS.has(host)) {
      return { valid: false, error: "validation.invalidUrlGloriafood" };
    }

    if (hasGloriaFoodMenuQueryIds(parsed.searchParams)) {
      return { valid: true };
    }

    const path = parsed.pathname || "";
    if (!path.startsWith(GLORIAFOOD_SHORT_URL_PATH_PREFIX)) {
      return { valid: false, error: "validation.invalidUrlGloriafood" };
    }

    const slug = path
      .slice(GLORIAFOOD_SHORT_URL_PATH_PREFIX.length)
      .replace(/\/$/, "");
    if (!slug) {
      return { valid: false, error: "validation.invalidUrlGloriafood" };
    }

    return { valid: true };
  } catch {
    return { valid: false, error: "validation.invalidUrlGloriafood" };
  }
};

const validateGloriaFoodTracker = (tracker) => {
  const trimmed = (tracker || "").trim();
  if (!trimmed) {
    return { valid: false, error: "validation.gloriafoodTrackerRequired" };
  }
  if (trimmed.length > 128 || /\s/.test(trimmed)) {
    return { valid: false, error: "validation.invalidGloriafoodTracker" };
  }
  return { valid: true };
};

const validateGloriaFoodForm = (form) => {
  const urlValidation = validateGloriaFoodUrl(form.url);
  if (!urlValidation.valid) return urlValidation;
  return validateGloriaFoodTracker(form.tracker);
};

/**
 * Menu importer cards configuration.
 * Add new importers (Uber Eats, Grubhub, etc.) with card config + modalConfig.
 */
export const IMPORTER_CARDS = [
  {
    id: "doordash",
    searchTerms: ["doordash"],
    titleKey: "doordash.cardTitle",
    descriptionKey: "doordash.cardDescription",
    importButtonKey: "doordash.importButton",
    logoColor: "#FF3008",
    modalConfig: {
      titleKey: "modal.title",
      subtitleKey: "modal.subtitle",
      urlLabelKey: "modal.urlLabel",
      urlPlaceholderKey: "modal.urlPlaceholder",
      urlHelperKey: "modal.urlHelper",
      statusImportingKey: "status.importing",
      includeAllMenuVariantsKey: "modal.includeAllMenuVariants",
      validateForm: validateDoorDashForm,
      useImport: () => useMenuImport("doordash"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants = true,
        userEmail = null,
      ) => ({
        doordashUrl: (form.url || "").trim(),
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants,
        userEmail: userEmail || undefined,
        useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
      }),
    },
  },
  {
    id: "ubereats",
    searchTerms: ["uber", "eats", "ubereats"],
    titleKey: "ubereats.cardTitle",
    descriptionKey: "ubereats.cardDescription",
    importButtonKey: "ubereats.importButton",
    logoColor: "#06C167",
    modalConfig: {
      titleKey: "modal.ubereatsTitle",
      subtitleKey: "modal.ubereatsSubtitle",
      urlLabelKey: "modal.ubereatsUrlLabel",
      urlPlaceholderKey: "modal.ubereatsUrlPlaceholder",
      urlHelperKey: "modal.ubereatsUrlHelper",
      statusImportingKey: "status.importingUbereats",
      includeAllMenuVariantsKey: "modal.includeAllMenuVariants",
      validateForm: validateUberEatsForm,
      useImport: () => useMenuImport("ubereats"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants = true,
        userEmail = null,
      ) => ({
        url: (form.url || "").trim(),
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants,
        userEmail: userEmail || undefined,
        useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
      }),
    },
  },
  {
    id: "ordering",
    searchTerms: ["ordering", "orderingplus", "ordering.co", "orderingco"],
    titleKey: "ordering.cardTitle",
    descriptionKey: "ordering.cardDescription",
    importButtonKey: "ordering.importButton",
    logoColor: "#7C3AED",
    modalConfig: {
      titleKey: "modal.orderingTitle",
      subtitleKey: "modal.orderingSubtitle",
      urlLabelKey: "modal.orderingUrlLabel",
      urlPlaceholderKey: "modal.orderingUrlPlaceholder",
      urlHelperKey: "modal.orderingUrlHelper",
      orderingProjectLabelKey: "modal.orderingProjectLabel",
      orderingProjectPlaceholderKey: "modal.orderingProjectPlaceholder",
      orderingBusinessSegmentLabelKey: "modal.orderingBusinessSegmentLabel",
      orderingBusinessSegmentPlaceholderKey:
        "modal.orderingBusinessSegmentPlaceholder",
      orderingStructuredHintKey: "modal.orderingStructuredHint",
      statusImportingKey: "status.importingOrdering",
      includeAllMenuVariantsKey: null,
      validateForm: validateOrderingForm,
      useImport: () => useMenuImport("ordering"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        _includeAllMenuVariants,
        userEmail = null,
      ) => {
        const url = (form.url || "").trim();
        const base = {
          businessIds,
          project,
          orderingApiBaseUrl,
          userEmail: userEmail || undefined,
          useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
        };
        if (url) {
          return { ...base, url };
        }
        return {
          ...base,
          orderingPlusProjectId:
            (form.orderingPlusProjectId || "").trim() || (project || "").trim(),
          orderingBusinessSegment: (form.orderingBusinessSegment || "").trim(),
        };
      },
    },
  },
  {
    id: "gloriafood",
    searchTerms: ["gloriafood", "gloria", "onlineorderpro"],
    titleKey: "gloriafood.cardTitle",
    descriptionKey: "gloriafood.cardDescription",
    importButtonKey: "gloriafood.importButton",
    logoColor: "#F15A24",
    modalConfig: {
      titleKey: "modal.gloriafoodTitle",
      subtitleKey: "modal.gloriafoodSubtitle",
      urlLabelKey: "modal.gloriafoodUrlLabel",
      urlPlaceholderKey: "modal.gloriafoodUrlPlaceholder",
      urlHelperKey: "modal.gloriafoodUrlHelper",
      trackerLabelKey: "modal.gloriafoodTrackerLabel",
      trackerPlaceholderKey: "modal.gloriafoodTrackerPlaceholder",
      trackerHelperKey: "modal.gloriafoodTrackerHelper",
      statusImportingKey: "status.importingGloriafood",
      includeAllMenuVariantsKey: null,
      validateForm: validateGloriaFoodForm,
      useImport: () => useMenuImport("gloriafood"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        _includeAllMenuVariants,
        userEmail = null,
      ) => ({
        url: (form.url || "").trim(),
        gloriafoodTracker: (form.tracker || "").trim(),
        businessIds,
        project,
        orderingApiBaseUrl,
        userEmail: userEmail || undefined,
        useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
      }),
    },
  },
];
